<template>
  <AppTimeline
    ref="timelineRef"
    :disabled
    :hide-slider
  >
    <UIPreloader v-if="isLoading" message="Loading analytics..." />
    <AppBreadcrumb
      v-else-if="!hideBreadcrumb"
      v-bind="{ path, items }"
      @click:item="setPath"
    >
      <UIButton
        v-if="isRefreshButtonVisible"
        :leading="ArrowPathRoundedSquareIcon"
        :label="refreshLabel"
        variant="secondary"
        fill="light"
        size="xxs"
        class="app-breadcrumb__refresh"
        @click="handleRefresh"
      />
    </AppBreadcrumb>
  </AppTimeline>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'
import { useResizeObserver, useThrottleFn } from '@vueuse/core'

import useAnalyticsStore from '@/store/analytics'

import { ArrowPathRoundedSquareIcon } from '@heroicons/vue/24/outline'

import { UIButton } from '@ui/buttons'
import { UIPreloader } from '@ui/core'
import { AppBreadcrumb, AppTimeline } from '@app'

type Props = {
  disabled?: boolean
  hideSlider?: boolean
  hideBreadcrumb?: boolean
}

defineProps<Props>()

defineOptions({
  name: 'DashboardTimeline',
})

const analyticsStore = useAnalyticsStore()

const timelineRef = useTemplateRef('timelineRef')

const isShortView = ref(false)

const isInit = computed(() => !analyticsStore.module.tree.isInit)

const isLoading = computed(
  () =>
    (analyticsStore.module.tree.isLoading || isInit.value) &&
    !analyticsStore.getError,
)

const items = computed(() => analyticsStore.module.tree.getFilterableNodes)
const path = computed(() => analyticsStore.getPath)

const isRefreshButtonVisible = computed(() => analyticsStore.isWaitingToRefresh)

const refreshLabel = computed(() => {
  return isShortView.value ? undefined : 'Refresh analytics'
})

const setPath = (value: string[]) => {
  analyticsStore.setPath(value)
}

const handleRefresh = () => {
  analyticsStore.markAsDeprecated()
}

useResizeObserver(timelineRef, useThrottleFn(entry => {
  isShortView.value = entry[0]?.contentRect.width < 640
}, 300))
</script>

<style>
.app-breadcrumb__refresh {
  @apply -my-2;

  .ui-button__icon {
    @apply size-4;
  }
}
</style>
